/**
 * De schrijfkant van de subsidiemodule.
 *
 * `useSubsidieRegelingen` doet uitsluitend SELECT. Daardoor was elke correctie
 * op een bedrag, een datum of een voorwaarde tot nu toe een migratie — de zes
 * regelingen zijn op 8 september 2026 via migratie 060 bijgewerkt omdat er geen
 * andere weg was. Dat schaalt niet: de bron verandert een paar keer per jaar
 * per regeling. Deze hook maakt er een handeling van.
 *
 * De leeskant blijft waar hij staat; deze hook muteert alleen en verklaart de
 * bestaande querysleutels ongeldig (`['sub_regelingen', jaar]` en de andere in
 * `useSubsidieRegelingen.ts:71-137`). De invalidatie gaat bewust op de prefix
 * zonder jaar: verandert het jaartal van een regeling, dan zijn twee jaargangen
 * tegelijk verouderd.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { SubsidieRegeling, SubsidieRegel, SubsidieBedrag } from '@/hooks/useSubsidieRegelingen';
import type { SubsidieIntakeData } from '@/hooks/useSubsidieIntake';
import {
  BOTSING_UITLEG, ONZEKER_UITLEG, duidSchrijffout, isZekerNietsGeschreven, opVersie,
  telGeraakteRijen, type SchemaStand,
} from '@/hooks/subsidie-schrijfrecht';
import {
  bewaakBevestiging, catalogusAfdruk, geefBevestigingVrij,
  type Catalogus, type Dossier, type Voorstel, type Voorvertoning,
} from '@/hooks/subsidie-voorvertoning';

/* -------------------------------------------------------------------------
 * De ENIGE plek waar de typegeneratie omzeild wordt.
 *
 * `src/integrations/supabase/types.ts` staat op slot (v1-guard) en is niet
 * opnieuw gegenereerd na migratie 060. Het gegenereerde `Database`-type kent
 * `sub_regelingen.bron_gecontroleerd_op` daardoor niet: gecontroleerd op
 * 8-9-2026 ontbreekt de kolom in Row, Insert én Update
 * (`types.ts:6468-6536`), terwijl `information_schema` hem wél teruggeeft
 * (date, nullable). PostgREST stuurt de kolom dus gewoon mee bij `select('*')`,
 * maar TypeScript ziet hem niet en weigert elk payload dat hem noemt.
 *
 * Vandaar precies twee ontsnappingen, allebei hier: `regelingenTabel()` voor de
 * schrijfkant en `leesBronGecontroleerdOp()` voor de leeskant. `sub_regels` en
 * `sub_bedragen` blijven volledig getypeerd — aan die twee schema's is niets
 * veranderd. Zodra types.ts opnieuw gegenereerd is kunnen beide helpers weg.
 * ---------------------------------------------------------------------- */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const regelingenTabel = () => (supabase as any).from('sub_regelingen');

export function leesBronGecontroleerdOp(regeling: SubsidieRegeling | null | undefined): string | null {
  const ruw = (regeling as { bron_gecontroleerd_op?: unknown } | null | undefined)?.bron_gecontroleerd_op;
  return typeof ruw === 'string' && ruw.length > 0 ? ruw : null;
}

/**
 * `bijgewerkt_door` staat sinds migratie 070 op `sub_regelingen` en sinds 083
 * ook op `sub_regels` en `sub_bedragen`. Geen van de drie staat in de
 * gegenereerde types (die zijn van vóór 060 en `types.ts` staat op slot), dus
 * lezen gaat via dezelfde ontsnapping als `leesBronGecontroleerdOp`.
 *
 * NULL betekent "niet via de app geschreven": SQL-editor, migratie, pg_cron of
 * service_role. Het scherm hoort dat niet als "door niemand" te tonen.
 */
/**
 * De `updated_at` van een rij, defensief gelezen.
 *
 * `sub_regels` en `sub_bedragen` krijgen die kolom pas met migratie 083, en de
 * gegenereerde types kennen hem sowieso niet (`types.ts` staat op slot). Is hij
 * er niet, dan komt hier `null` uit en laat de schrijfactie de versiefilter
 * weg — dan gedraagt het scherm zich precies zoals vóór 083, in plaats van te
 * breken op een kolom die nog niet bestaat.
 */
export function leesUpdatedAt(rij: unknown): string | null {
  const ruw = (rij as { updated_at?: unknown } | null | undefined)?.updated_at;
  return typeof ruw === 'string' && ruw.length > 0 ? ruw : null;
}

export function leesBijgewerktDoor(rij: unknown): string | null {
  const ruw = (rij as { bijgewerkt_door?: unknown } | null | undefined)?.bijgewerkt_door;
  return typeof ruw === 'string' && ruw.length > 0 ? ruw : null;
}

/* ---------------------------------------------------------------------- */

/**
 * Wat het formulier van een regeling oplevert. Bewust een eigen interface en
 * niet `Partial<SubsidieRegeling>`: zo blijft het payload dat door de
 * ongetypeerde tafelverwijzing hierboven gaat tóch getypeerd.
 */
export interface RegelingInvoer {
  code: string;
  jaar: number;
  naam: string;
  korte_naam: string;
  type: SubsidieRegeling['type'];
  status: SubsidieRegeling['status'];
  aanvraag_open: string | null;
  aanvraag_sluit: string | null;
  concept_vanaf: string | null;
  budget_totaal: number | null;
  budget_restant: number | null;
  budget_toelichting: string | null;
  max_subsidie_per_aanvrager: number | null;
  min_subsidiebedrag: number | null;
  subsidie_percentage_max: number | null;
  rvo_url: string | null;
  opmerkingen: string | null;
  samenvatting: string | null;
  bron_gecontroleerd_op: string | null;
}

export interface RegelInvoer {
  regeling_id: string;
  veld: string;
  operator: string;
  /** Nooit null: `sub_regels.waarde` is jsonb NOT NULL. Zie `intake-velden.ts`. */
  waarde: unknown;
  verplicht: boolean;
  beschrijving: string;
  foutmelding: string | null;
  groep: string | null;
  sort_order: number;
}

export interface BedragInvoer {
  regeling_id: string;
  categorie: string;
  subcategorie: string | null;
  bedrag_type: SubsidieBedrag['bedrag_type'];
  bedrag: number;
  max_bedrag: number | null;
  max_eenheden: number | null;
  min_eenheden: number | null;
  bedrijfsgrootte: string | null;
  opmerkingen: string | null;
  sort_order: number;
}

/**
 * Wat élke schrijfactie in dit scherm naast zijn velden meedraagt.
 *
 * De voorvertoning hoort bij de handeling, niet bij het scherm: de mutatie
 * berekent niet zelf opnieuw of er iets verschuift, hij krijgt de uitkomst mee
 * die de gebruiker heeft gezien en toetst of de bevestiging dáárbij hoort.
 * Wijzigt het formulier daarna nog iets, dan verandert de sleutel mee en valt
 * de mutatie om — precies de bedoeling.
 */
export interface MetBevestiging {
  /** Wat er precies geschreven gaat worden. De grendel toetst hierop. */
  voorstel: Voorstel;
  /** De jaargang waarover de voorvertoning is gemeten. Bepaalt de verse stand. */
  jaar: number;
  voorvertoning: Voorvertoning;
  bevestiging?: string | null;
  /**
   * De `updated_at` van de rij zoals de voorvertoning hem zag, of `null` bij
   * een INSERT en zolang migratie 083 nog niet is toegepast.
   *
   * Zonder deze filter overschrijft de laatste schrijver de eerste zonder dat
   * iemand het merkt, en — erger — was de voorvertoning gerekend op een stand
   * die inmiddels niet meer bestond: het bedrag op de knop klopte dan niet.
   * `telGeraakteRijen` maakt van nul geraakte rijen een harde fout, dus de
   * botsing komt luid in beeld in plaats van stil goed te gaan.
   */
  gezienOp?: string | null;
}

/** De vier CHECK-lijsten uit de database, gemeten op 8-9-2026 via `pg_constraint`. */
export const REGELING_STATUSSEN: readonly SubsidieRegeling['status'][] = ['open', 'gesloten', 'uitgeput', 'gepland', 'voorbehoud'];
export const REGELING_TYPES: readonly SubsidieRegeling['type'][] = ['subsidie', 'fiscaal'];
export const BEDRAG_TYPES: readonly SubsidieBedrag['bedrag_type'][] = ['vast', 'per_kwh', 'per_kw', 'percentage', 'aftrek'];
export const BEDRIJFSGROOTTES: readonly string[] = ['klein', 'middel', 'mkb', 'groot'];

/**
 * Wie wat mag, staat in `@/hooks/subsidie-schrijfrecht` — één pure functie,
 * zodat het scherm, de dialogen en de test hetzelfde antwoord lezen.
 *
 * Tot 12-09-2026 stond hier `BEHEER_ROLLEN = ['admin','super_admin']` en hing
 * het scherm alle zeven knoppen aan die ene lijst. Migratie 070 (regelingen
 * bijwerken) en 083 (regels en bedragen bijwerken en toevoegen) maken dat
 * onwaar: een `crm_user` mag nu een deel, en welk deel verschilt per handeling
 * én per kolom.
 */
export {
  AUDITKOLOMMEN,
  BEDRAG_IDENTITEIT,
  REGELING_VRIJ_070,
  BEDRAG_VRIJ,
  CATALOGUS_ROLLEN,
  CRM_ROLLEN,
  REDEN,
  REGELING_IDENTITEIT,
  REGELING_VRIJ,
  REGEL_IDENTITEIT,
  REGEL_VRIJ,
  schrijfrechtenVoorRol,
  type BeheerRol,
  type Schrijfrechten,
} from '@/hooks/subsidie-schrijfrecht';

/**
 * De dossiers waartegen de voorvertoning rekent: ÉLKE intake.
 *
 * Eerst stond hier een filter op `sub_matches` — "een dossier onder een
 * regeling" — en dat was fout, op twee manieren die elkaar versterken:
 *
 *  * Intake 98916b2c heeft zes gevulde datavelden en volledigheid 55%, maar
 *    NUL matches. Hij kwam daardoor in geen enkele voorvertoning voor. (De
 *    intake die wél zes matches heeft en tóch leeg is, heet "Test" en heeft
 *    nul datavelden — precies andersom dus.)
 *  * `sub_matches` loopt achter: `berekend_op` staat op 2026-09-11 en Flex-E
 *    staat er op EUR 39.992 waar de kern vandaag EUR 36.000 geeft. Een tabel
 *    die niet meer klopt mag niet bepalen wát er vergeleken wordt.
 *
 * Bij 0,12 ms per voorvertoning is "gewoon alles doorrekenen" bovendien
 * goedkoper dan de vraag beantwoorden welke intakes meetellen. Groeit
 * `sub_intakes` ooit naar duizenden rijen, dan is dít de plek om te knijpen —
 * en dan op een gemeten grens, niet op een tabel die achterloopt.
 *
 * FAALT DEZE QUERY, dan is er geen voorvertoning — en dan hoort er ook niet
 * geschreven te worden. Het paneel zet de opslaanknoppen uit.
 */
/**
 * Staat migratie 083 in de database?
 *
 * Eén SELECT op een kolom die alleen 083 aanmaakt. Bestaat hij niet, dan geeft
 * PostgREST 42703 of PGRST204 en weten we dat de oude stand van 070 geldt.
 *
 * Waarom dit er is: zonder deze meting beloofde het scherm een crm_user rechten
 * die de database niet geeft — regels en bedragen bewerken strandde stil op nul
 * rijen, en vijf regelingvelden kwamen terug met 42501. Het antwoord op "wat
 * mag ik" hoort gemeten te worden, niet aangenomen.
 *
 * Bij twijfel de SMALLE stand: faalt de probe om een andere reden, dan telt 083
 * als niet-toegepast. Dan belooft het scherm te weinig in plaats van te veel.
 */
export function useSubsidieSchemaStand() {
  return useQuery({
    queryKey: ['sub_schema_stand'],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<SchemaStand> => {
      const { error } = await supabase.from('sub_regels').select('updated_at').limit(1);
      return { migratie083: !error };
    },
  });
}

/**
 * Namen bij de `bijgewerkt_door`-ID's die het beheerscherm toont ("laatst
 * gewijzigd door …"). Eén query voor de hele lijst, niet één per rij.
 *
 * Een ID zonder profiel komt niet in het resultaat en het scherm valt dan terug
 * op `account 1a2b3c4d`. Dat is geen fout: `bijgewerkt_door` heeft bewust geen
 * foreign key naar auth.users (zie 083), dus een verwijderd account laat een ID
 * achter zonder naam.
 *
 * Deze hook werd door SubsidieBeheerPaneel aangeroepen maar was nooit
 * geschreven. De tests importeren de hooks en niet het scherm, en check:types
 * is alleen fataal binnen de Project Builder, dus het glipte door elke poort —
 * in een productiebuild is een ontbrekende named export een harde fout.
 */
export function useSchrijversNamen(ids: ReadonlyArray<string | null>) {
  const uniek = [...new Set(ids.filter((id): id is string => !!id))].sort();
  return useQuery({
    queryKey: ['sub_schrijvers', uniek],
    enabled: uniek.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Record<string, string>> => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, display_name, email')
        .in('id', uniek);
      if (error) throw error;
      const namen: Record<string, string> = {};
      for (const p of data ?? []) {
        const naam = p.display_name?.trim() || p.email;
        if (naam) namen[p.id] = naam;
      }
      return namen;
    },
  });
}

export function useSubsidieDossiers() {
  return useQuery({
    queryKey: ['sub_dossiers'],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<{ dossiers: Dossier[] }> => {
      const { data, error } = await supabase.from('sub_intakes').select('id, naam, data');
      if (error) throw error;
      const dossiers = (data ?? []).map(rij => {
        const r = rij as { id: string; naam?: string | null; data?: unknown };
        return {
          intakeId: r.id,
          naam: (r.naam ?? '').trim() || `dossier ${r.id.slice(0, 8)}`,
          data: (r.data ?? {}) as SubsidieIntakeData,
        };
      });
      dossiers.sort((a, b) => a.naam.localeCompare(b.naam));
      return { dossiers };
    },
  });
}

/**
 * De stand van de catalogus, VERS uit de database, vlak vóór het schrijven.
 *
 * Dit is de reparatie van het zwaarste gat: de grendel hing aan het voorstel en
 * aan de uitkomst, maar nergens aan de catalogus waarop die uitkomst gemeten
 * was. `gezienOp` dekt alleen de ene rij die je schrijft, dus een collega die
 * ándere rijen wijzigde bleef onzichtbaar — gemeten: A verlaagt de MIA-45%-rij
 * terwijl B de 27%- en 36%-rij weghaalt, werkelijk effect -EUR 42.345, en de
 * voorvertoning zei "geen verschil".
 *
 * React Query helpt hier niet: de cache kán muf zijn, en juist dán gaat het
 * mis. Daarom een echte query, niet de cache. Drie selects over 6 + 15 + 46
 * rijen; dat kost minder dan de schrijfactie zelf.
 *
 * Dezelfde filters als `useSubsidieRegelingen` (`.eq('jaar', jaar)` en de
 * inner join), zodat de afdruk over exact dezelfde verzameling gaat als die
 * waarop de voorvertoning is gerekend.
 */
async function verseCatalogusAfdruk(jaar: number): Promise<string> {
  const [regelingen, regels, bedragen] = await Promise.all([
    supabase.from('sub_regelingen').select('*').eq('jaar', jaar),
    supabase.from('sub_regels').select('*, sub_regelingen!inner(jaar)').eq('sub_regelingen.jaar', jaar),
    supabase.from('sub_bedragen').select('*, sub_regelingen!inner(jaar)').eq('sub_regelingen.jaar', jaar),
  ]);
  if (regelingen.error) throw regelingen.error;
  if (regels.error) throw regels.error;
  if (bedragen.error) throw bedragen.error;
  return catalogusAfdruk({
    regelingen: (regelingen.data ?? []) as Catalogus['regelingen'],
    regels: (regels.data ?? []) as Catalogus['regels'],
    bedragen: (bedragen.data ?? []) as Catalogus['bedragen'],
  });
}

export function useSubsidieBeheer() {
  const queryClient = useQueryClient();

  const verversAlles = () => {
    for (const sleutel of ['sub_regelingen', 'sub_regels', 'sub_bedragen', 'sub_documenten', 'sub_combinaties']) {
      queryClient.invalidateQueries({ queryKey: [sleutel] });
    }
    // Een gewijzigde regel of bedrag verandert elke uitkomst die daarop leunt.
    queryClient.invalidateQueries({ queryKey: ['sub_matches'] });
  };

  /**
   * Eén plek waar een mislukte schrijfactie in beeld komt, en nooit als succes.
   *
   * `duidSchrijffout` scheidt drie dingen die er verschillend uitzien maar
   * allemaal "het is niet opgeslagen" betekenen: het kolomslot uit 070/083
   * (SQLSTATE 42501 mét `tabel.kolom` in de tekst), een RLS-weigering op een
   * hele rij (42501 zonder kolomnaam), en de stilte van nul geraakte rijen.
   * Een toast van tien seconden voor de eerste twee: dat zijn grenzen die de
   * gebruiker moet kunnen lezen, niet flitsen.
   */
  /**
   * Een mislukte schrijfactie geeft zijn bevestiging ALLEEN terug als vaststaat
   * dat er niets is geschreven.
   *
   * Hier zat een gat, en het was het ergste van de drie. Deze functie gaf de
   * sleutel onvoorwaardelijk vrij, bij élke `onError`. Bij een netwerk- of
   * timeoutfout heeft de INSERT Postgres echter wél kunnen bereiken: de rij
   * kan er staan. De dialoog bleef intussen open met een werkende knop, dus een
   * tweede klik maakte een tweede rij — precies het duplicaat waarvoor dit
   * mechanisme bestaat (één extra SPULA-tarief: EUR 212.000 -> EUR 270.000).
   *
   * `isZekerNietsGeschreven` trekt de grens: onze eigen grendel, nul geraakte
   * rijen, of een echte SQLSTATE/PostgREST-code. Al het andere telt als
   * onzeker, en dan blijft de sleutel verbruikt en zegt het scherm wat de
   * gebruiker moet doen: verversen en kijken of hij er al staat.
   */
  function meldFout(wat: string) {
    return (error: unknown, variabelen?: { bevestiging?: string | null }) => {
      const zeker = isZekerNietsGeschreven(error);
      if (zeker) geefBevestigingVrij(variabelen?.bevestiging);
      const fout = duidSchrijffout(error, wat);
      toast.error(zeker ? fout.melding : `${fout.melding} ${ONZEKER_UITLEG}`, {
        duration: fout.soort === 'onbekend' ? 6000 : 12000,
        description: fout.soort === 'kolom-op-slot'
          ? 'Dit is een identiteitskolom; die hoort niet via het beheerscherm te wijzigen.'
          : fout.soort === 'niet-bevestigd'
            ? 'Bekijk de voorvertoning en bevestig daar het bedrag.'
            : undefined,
      });
    };
  }

  const bewaarRegeling = useMutation({
    mutationFn: async ({ id, velden, voorstel, jaar, voorvertoning, bevestiging, gezienOp }: { id?: string; velden: RegelingInvoer } & MetBevestiging) => {
      // Vóór elke aanraking van Supabase. Een bevestiging die alleen in het
      // scherm bestaat is geen bevestiging: een knop die twee keer vuurt of een
      // latere aanroeper die de dialoog overslaat komt anders gewoon langs.
      bewaakBevestiging(voorvertoning, voorstel, await verseCatalogusAfdruk(jaar), bevestiging);
      // `updated_at` gaat niet meer mee: 083 maakt hem een auditkolom die de
      // trigger zet, wat de client ook stuurt.
      if (id) {
        const { data, error } = await opVersie(
          regelingenTabel().update(velden).eq('id', id), gezienOp,
        ).select('id');
        if (error) throw error;
        telGeraakteRijen(data, 'De regeling', 1, gezienOp ? BOTSING_UITLEG : '');
        return id;
      }
      const { data, error } = await regelingenTabel().insert(velden).select('id');
      if (error) throw error;
      telGeraakteRijen(data, 'De nieuwe regeling');
      return (data as { id: string }[])[0].id;
    },
    onSuccess: () => { verversAlles(); toast.success('Regeling opgeslagen'); },
    onError: meldFout('Regeling opslaan'),
  });

  const verwijderRegeling = useMutation({
    mutationFn: async ({ id, voorstel, jaar, voorvertoning, bevestiging, gezienOp }: { id: string } & MetBevestiging) => {
      bewaakBevestiging(voorvertoning, voorstel, await verseCatalogusAfdruk(jaar), bevestiging);
      // De FK's van sub_regels en sub_bedragen staan op ON DELETE CASCADE
      // (gemeten in pg_constraint), dus dit neemt de regels en bedragen mee.
      const { data, error } = await regelingenTabel().delete().eq('id', id).select('id');
      if (error) throw error;
      telGeraakteRijen(data, 'De regeling');
    },
    onSuccess: () => { verversAlles(); toast.success('Regeling verwijderd'); },
    onError: meldFout('Regeling verwijderen'),
  });

  /**
   * De handeling waar `bron_gecontroleerd_op` voor bedoeld is: iemand heeft de
   * RVO-pagina bekeken en niets hoeven wijzigen. Zonder deze knop zou de datum
   * alleen kunnen verspringen door de regeling zelf te bewerken.
   */
  const markeerGecontroleerd = useMutation({
    // Geen voorvertoning. `bron_gecontroleerd_op` staat in TOONKOLOMMEN: de
    // rekenkern leest hem in geen enkele tak, dus deze knop kan per definitie
    // geen bedrag verschuiven. Een bevestiging eisen zou de handeling waar de
    // kolom voor bestaat — "ik heb de RVO-pagina nagelopen" — onnodig zwaar
    // maken, en dat is precies wat het besluit van Stan wil voorkomen.
    mutationFn: async ({ id, datum }: { id: string; datum: string }) => {
      const { data, error } = await regelingenTabel()
        .update({ bron_gecontroleerd_op: datum })
        .eq('id', id)
        .select('id');
      if (error) throw error;
      telGeraakteRijen(data, 'De controledatum');
    },
    onSuccess: () => { verversAlles(); toast.success('Bron gemarkeerd als gecontroleerd'); },
    onError: meldFout('Controledatum bijwerken'),
  });

  const bewaarRegel = useMutation({
    mutationFn: async ({ id, velden, voorstel, jaar, voorvertoning, bevestiging, gezienOp }: { id?: string; velden: RegelInvoer } & MetBevestiging) => {
      bewaakBevestiging(voorvertoning, voorstel, await verseCatalogusAfdruk(jaar), bevestiging);
      // `waarde` is jsonb NOT NULL. `bouwRegelWaarde` levert daarom ook voor
      // is_true / is_false / is_not_null een echte waarde; hier nog een laatste
      // vangnet zodat een lege waarde niet als constraint-fout terugkomt.
      if (velden.waarde === undefined || velden.waarde === null) {
        throw new Error('De waarde van een regel mag niet leeg zijn (sub_regels.waarde is NOT NULL).');
      }
      const rij = { ...velden, waarde: velden.waarde as never };
      if (id) {
        const { data, error } = await opVersie(
          supabase.from('sub_regels').update(rij).eq('id', id), gezienOp,
        ).select('id');
        if (error) throw error;
        telGeraakteRijen(data, 'De regel', 1, gezienOp ? BOTSING_UITLEG : '');
        return id;
      }
      const { data, error } = await supabase.from('sub_regels').insert(rij).select('id');
      if (error) throw error;
      telGeraakteRijen(data, 'De nieuwe regel');
      return data[0].id;
    },
    onSuccess: () => { verversAlles(); toast.success('Regel opgeslagen'); },
    onError: meldFout('Regel opslaan'),
  });

  const verwijderRegel = useMutation({
    mutationFn: async ({ id, voorstel, jaar, voorvertoning, bevestiging, gezienOp }: { id: string } & MetBevestiging) => {
      bewaakBevestiging(voorvertoning, voorstel, await verseCatalogusAfdruk(jaar), bevestiging);
      const { data, error } = await supabase.from('sub_regels').delete().eq('id', id).select('id');
      if (error) throw error;
      telGeraakteRijen(data, 'De regel');
    },
    onSuccess: () => { verversAlles(); toast.success('Regel verwijderd'); },
    onError: meldFout('Regel verwijderen'),
  });

  const bewaarBedrag = useMutation({
    mutationFn: async ({ id, velden, voorstel, jaar, voorvertoning, bevestiging, gezienOp }: { id?: string; velden: BedragInvoer } & MetBevestiging) => {
      bewaakBevestiging(voorvertoning, voorstel, await verseCatalogusAfdruk(jaar), bevestiging);
      if (id) {
        const { data, error } = await opVersie(
          supabase.from('sub_bedragen').update(velden).eq('id', id), gezienOp,
        ).select('id');
        if (error) throw error;
        telGeraakteRijen(data, 'Het bedrag', 1, gezienOp ? BOTSING_UITLEG : '');
        return id;
      }
      const { data, error } = await supabase.from('sub_bedragen').insert(velden).select('id');
      if (error) throw error;
      telGeraakteRijen(data, 'Het nieuwe bedrag');
      return data[0].id;
    },
    onSuccess: () => { verversAlles(); toast.success('Bedrag opgeslagen'); },
    onError: meldFout('Bedrag opslaan'),
  });

  const verwijderBedrag = useMutation({
    mutationFn: async ({ id, voorstel, jaar, voorvertoning, bevestiging, gezienOp }: { id: string } & MetBevestiging) => {
      bewaakBevestiging(voorvertoning, voorstel, await verseCatalogusAfdruk(jaar), bevestiging);
      const { data, error } = await supabase.from('sub_bedragen').delete().eq('id', id).select('id');
      if (error) throw error;
      telGeraakteRijen(data, 'Het bedrag');
    },
    onSuccess: () => { verversAlles(); toast.success('Bedrag verwijderd'); },
    onError: meldFout('Bedrag verwijderen'),
  });

  return {
    bewaarRegeling,
    verwijderRegeling,
    markeerGecontroleerd,
    bewaarRegel,
    verwijderRegel,
    bewaarBedrag,
    verwijderBedrag,
    isBezig:
      bewaarRegeling.isPending || verwijderRegeling.isPending || markeerGecontroleerd.isPending
      || bewaarRegel.isPending || verwijderRegel.isPending
      || bewaarBedrag.isPending || verwijderBedrag.isPending,
  };
}

export type { SubsidieRegel, SubsidieBedrag };
